import api from './api';
import { 
  LogSheet, 
  LogSheetFormData, 
  LogSheetsResponse, 
  LogSheetFilters 
} from '../types/logSheetTypes';

class LogSheetService {
  async getLogSheets(filters: LogSheetFilters = {}): Promise<LogSheetsResponse> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        params.append(key, String(value));
      }
    });

    const response = await api.get(`/log-sheets?${params}`);
    return response.data;
  }

  async getLogSheet(id: number): Promise<LogSheet> {
    const response = await api.get(`/log-sheets/${id}`);
    return response.data;
  }

  async createLogSheet(data: LogSheetFormData): Promise<LogSheet> {
    const response = await api.post('/log-sheets', data);
    return response.data.logSheet;
  }

  async updateLogSheet(id: number, data: LogSheetFormData): Promise<LogSheet> {
    const response = await api.put(`/log-sheets/${id}`, data);
    return response.data.logSheet;
  }

  async deleteLogSheet(id: number): Promise<void> {
    await api.delete(`/log-sheets/${id}`);
  }

  async exportToExcel(filters: any = {}): Promise<any> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        params.append(key, String(value));
      }
    });

    const response = await api.get(`/log-sheets/export?${params}`, {
      responseType: 'blob'
    });
    return response;
  }

  // Helper function to format log code
  formatLogCode(logCode: string | null): string {
    return logCode || 'N/A';
  }

  // Helper function to format drilling depth
  formatDepth(feet: number | null): string {
    if (!feet) return '0 ft';
    return `${feet.toLocaleString()} ft`;
  }

  // Helper function to format yield
  formatYield(yield_val: number | null): string {
    if (!yield_val) return '0';
    return yield_val.toLocaleString();
  }

  // Helper function to get status color based on completion
  getStatusColor(logSheet: LogSheet): string {
    if (logSheet.is_cancelled === 'Y') return 'bg-red-100 text-red-800';
    if (logSheet.is_billed === 'Y') return 'bg-green-100 text-green-800';
    if (logSheet.no_of_feet && logSheet.no_of_feet > 0) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  }

  // Helper function to get status text
  getStatusText(logSheet: LogSheet): string {
    if (logSheet.is_cancelled === 'Y') return 'Cancelled';
    if (logSheet.is_billed === 'Y') return 'Billed';
    if (logSheet.no_of_feet && logSheet.no_of_feet > 0) return 'Completed';
    return 'In Progress';
  }

  // Helper function to calculate drilling efficiency
  calculateEfficiency(logSheet: LogSheet): number {
    if (!logSheet.no_of_feet || !logSheet.from_diesel || !logSheet.to_diesel) return 0;
    const fuelUsed = logSheet.to_diesel - logSheet.from_diesel;
    if (fuelUsed <= 0) return 0;
    return logSheet.no_of_feet / fuelUsed; // feet per liter
  }

  // Helper function to format RPM range
  formatRPMRange(from: number | null, to: number | null): string {
    if (!from && !to) return 'N/A';
    if (!to) return `${from || 0}`;
    if (!from) return `${to}`;
    return `${from}-${to}`;
  }

  // Helper function to format diesel consumption
  formatDieselConsumption(from: number | null, to: number | null): string {
    if (!from || !to) return 'N/A';
    const consumption = to - from;
    return `${consumption.toFixed(2)}L`;
  }

  // Helper function to format temperature
  formatTemperature(temp: number | null): string {
    if (!temp) return 'N/A';
    return `${temp}°C`;
  }
}

export const logSheetService = new LogSheetService();
export default logSheetService;